type StartHandler = () => void;

let startHandler: StartHandler | null = null;
let tutorialLinkHandler: StartHandler | null = null;
let tutorialSkipHandler: StartHandler | null = null;

export type BannerTone = 'success' | 'failure' | 'info';

export interface StartOverlayConfig {
  role: string;
  roleDesc: string;
  goal: string;
  buttonText: string;
  avatarUrl?: string;
  banner?: { text: string; tone: BannerTone };
  tutorial?: { mode: 'skip' | 'replay'; onClick: () => void };
}

export function initOverlay(): void {
  const button = document.getElementById('start-button');
  button?.addEventListener('click', (e) => {
    e.preventDefault();
    startHandler?.();
  });

  document.getElementById('start-tutorial-link')?.addEventListener('click', (e) => {
    e.preventDefault();
    tutorialLinkHandler?.();
  });

  document.getElementById('tutorial-skip')?.addEventListener('click', (e) => {
    e.preventDefault();
    tutorialSkipHandler?.();
  });
}

export function onStartButton(fn: StartHandler): void {
  startHandler = fn;
}

export function onTutorialSkip(fn: StartHandler | null): void {
  tutorialSkipHandler = fn;
}

export function showStartOverlay(cfg: StartOverlayConfig): void {
  const overlay = document.getElementById('start-overlay');
  const role = document.getElementById('start-role');
  const desc = document.getElementById('start-role-desc');
  const goal = document.getElementById('start-goal');
  const banner = document.getElementById('start-banner');
  const btn = document.getElementById('start-button');
  const avatar = document.getElementById('start-avatar') as HTMLImageElement | null;
  const tutLink = document.getElementById('start-tutorial-link');
  if (!overlay || !role || !desc || !goal || !banner || !btn) return;

  role.textContent = cfg.role;
  desc.textContent = cfg.roleDesc;
  goal.textContent = cfg.goal;
  btn.textContent = cfg.buttonText;

  if (avatar) {
    if (cfg.avatarUrl) {
      avatar.src = cfg.avatarUrl;
      avatar.removeAttribute('hidden');
    } else {
      avatar.removeAttribute('src');
      avatar.setAttribute('hidden', '');
    }
  }

  if (cfg.banner) {
    banner.textContent = cfg.banner.text;
    banner.dataset.tone = cfg.banner.tone;
    banner.removeAttribute('hidden');
  } else {
    banner.setAttribute('hidden', '');
    delete banner.dataset.tone;
  }

  if (tutLink) {
    if (cfg.tutorial) {
      tutLink.textContent = cfg.tutorial.mode === 'skip' ? 'Skip tutorial' : 'Replay tutorial';
      tutLink.removeAttribute('hidden');
      tutorialLinkHandler = cfg.tutorial.onClick;
    } else {
      tutLink.setAttribute('hidden', '');
      tutorialLinkHandler = null;
    }
  }

  overlay.removeAttribute('hidden');
}

export function hideStartOverlay(): void {
  document.getElementById('start-overlay')?.setAttribute('hidden', '');
  // The just-clicked button keeps focus while hidden. On macOS with Full
  // Keyboard Access on, the focused button intercepts arrow keys for spatial
  // navigation. Hand focus to the Phaser canvas so arrows reach the game.
  const canvas = document.querySelector('#game canvas') as HTMLCanvasElement | null;
  if (canvas) {
    canvas.focus({ preventScroll: true });
  } else if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
}

export function showCompliancePopup(rejectedCount: number, iconDataUrl: string): void {
  const popup = document.getElementById('compliance-popup');
  const num = document.getElementById('compliance-num');
  const noun = document.getElementById('compliance-noun');
  const icon = document.getElementById('compliance-icon') as HTMLImageElement | null;
  if (!popup || !num || !noun || !icon) return;
  num.textContent = String(rejectedCount);
  noun.textContent = rejectedCount === 1 ? 'entry' : 'entries';
  icon.src = iconDataUrl;
  popup.removeAttribute('hidden');
  void popup.offsetWidth;
}

export function hideCompliancePopup(): void {
  document.getElementById('compliance-popup')?.setAttribute('hidden', '');
}

export function showTutorialBanner(stepLabel: string, text: string): void {
  const banner = document.getElementById('tutorial-banner');
  const stepEl = document.getElementById('tutorial-step');
  const textEl = document.getElementById('tutorial-text');
  if (!banner || !stepEl || !textEl) return;
  stepEl.textContent = stepLabel;
  textEl.textContent = text;
  banner.removeAttribute('hidden');
}

export function hideTutorialBanner(): void {
  document.getElementById('tutorial-banner')?.setAttribute('hidden', '');
}

export function showUpdateBanner(onReload: () => void): void {
  const banner = document.getElementById('update-banner');
  if (!banner) return;
  banner.removeAttribute('hidden');
  banner.onclick = (e) => {
    e.preventDefault();
    onReload();
  };
}
